import { v4 as uuidv4 } from 'uuid';

import { getEnvVariable } from '~/dev/utils/getEnvVariable';

export interface AdminConsentResult {
  success: boolean;
  state?: string;
  tenantId?: string;
  error?: string;
  errorDescription?: string;
}

interface AdminConsentOptions {
  redirectUri?: string;
  state?: string;
}

/**
 * Generates the Microsoft admin consent URL.
 * This URL should be opened in a new window or navigated to directly.
 *
 * @param options - Optional configuration for the consent URL
 * @returns The admin consent URL
 */
export function getAdminConsentUrl(options: AdminConsentOptions = {}): string {
  const clientId = getEnvVariable('EVISORT_SPE_CLIENT_ID');

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: `${window.location.origin}`,
    scope: 'https://graph.microsoft.com/.default',
  });

  if (options.state) {
    params.append('state', options.state);
  }

  // we use organizations since is a multitenant app
  return `https://login.microsoftonline.com/organizations/v2.0/adminconsent?${params.toString()}`;
}

/**
 * Opens admin consent in a popup window.
 * Returns a promise that resolves when consent is complete.
 *
 * @param options - Optional configuration for the consent popup
 * @returns Promise resolving to the consent result
 */
export function openAdminConsentPopup(
  options: AdminConsentOptions = {},
): Promise<AdminConsentResult> {
  return new Promise((resolve, reject) => {
    const state = options.state || uuidv4();
    const redirectUri = options.redirectUri || `${window.location.origin}`;

    const consentUrl = getAdminConsentUrl({ ...options, state, redirectUri });

    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      consentUrl,
      'adminConsent',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`,
    );

    if (!popup) {
      reject(
        new Error('Failed to open popup. Please allow popups and try again.'),
      );
      return;
    }

    // Poll for popup closure and check for redirect
    const pollInterval = setInterval(() => {
      try {
        // Check if popup was redirected back to our domain
        if (popup.location.origin === window.location.origin) {
          const result = parseAdminConsentCallback(popup.location.href);
          clearInterval(pollInterval);
          popup.close();

          if (result.state && result.state !== state) {
            reject(new Error('State mismatch. Possible CSRF attack.'));
            return;
          }

          resolve(result);
        }
      } catch {
        // Cross-origin error - popup is still on Microsoft domain
        return;
      }

      if (popup.closed) {
        clearInterval(pollInterval);
        reject(new Error('Admin consent popup was closed before completing.'));
      }
    }, 500);
  });
}

/**
 * Parses the admin consent callback URL to extract the result.
 * Call this on the redirect URI page to determine if consent was successful.
 *
 * @param url - The callback URL (defaults to current window location)
 * @returns The parsed consent result
 */
export function parseAdminConsentCallback(
  url: string = window.location.href,
): AdminConsentResult & { state?: string } {
  const urlObj = new URL(url);
  const params = urlObj.searchParams;

  const adminConsent = params.get('admin_consent');
  const tenantId = params.get('tenant');
  const error = params.get('error');
  const errorDescription = params.get('error_description');
  const state = params.get('state');

  if (error) {
    return {
      success: false,
      error,
      errorDescription: errorDescription || undefined,
      state: state || undefined,
    };
  }

  if (adminConsent?.toLowerCase() === 'true' && tenantId && state) {
    return {
      success: true,
      tenantId,
      state,
    };
  }

  return {
    success: false,
    error: 'unknown',
    errorDescription: 'Could not determine consent status from callback URL',
  };
}
